'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api/client';

/**
 * Hook for canceling active subscription
 * DELETE /api/v1/billing/subscription
 *
 * Cancels subscription at end of current billing period
 * Invalidates subscription and billing-summary queries
 */
export function useCancelSubscription() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // The endpoint returns 204 No Content on success, so success is determined
      // by response.ok alone — requiring a body would reject a valid cancellation.
      const { error, response } = await apiClient.DELETE(
        '/api/v1/billing/subscription',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (error || !response.ok) {
        throw new Error('Failed to cancel subscription');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
    },
  });
}
